import type { SearchResultItem } from '../../types';
import type { DefaultTheme } from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import { useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useQueryClient } from '@tanstack/react-query';
import { Body, Button, Divider, Icon, Stack, Utility, Col, Row, Toast } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { MixPanel } from '~/common';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import { MixpanelEvents } from '~/elc/features/library/constants';
import { GET_LIBRARY_KEY } from '~/elc/features/library/api';
import { useDateFormatter } from '~/features/temporal';
import {
  type CMSAssetItem,
  hasValidCMSFileExtension,
  useFileDownloader,
  useCreateAssetAuditData,
  CMSAssetAuditAction,
  CMSTopLevelContentTypes,
  useGetAssetDetails,
} from '~/elc/features/cms';
import { getRedirectUrlForSearchResult, getIconPropsSearchResult } from '../../utils';
import { ACTIONS_ICON, TYPE_DICTIONARY, LearnerSearchMixpanelEvents } from '../../constants';
import {
  ResultTitle,
  IconWrapper,
  ResultBodyStack,
  FooterWrapper,
  InfoWrapper,
  TitleBar,
  ActionIcons,
  ActionIcon,
} from './CMSSearchResult.styles';

type AdvancedSearchResultProps = {
  resultItem: SearchResultItem;
  resultTarget?: '_self' | '_blank';
};

type ResultIconProps = {
  type: string;
  theme: DefaultTheme;
};

type ActionBarProps = {
  resultItem: SearchResultItem;
  asset: CMSAssetItem;
  theme: DefaultTheme;
};

const ResultIcon = ({ type, theme }: ResultIconProps) => {
  const iconProps = getIconPropsSearchResult({ type, colors: theme.colors });
  return <Icon name={iconProps.name} color={iconProps.color} />;
};

const ActionsBar = ({ resultItem, asset, theme }: ActionBarProps) => {
  const downloadName = hasValidCMSFileExtension(asset.name) ? asset.name : `${asset.name}.${asset.fileType}`;
  const {
    attachmentInfo: { allowDownloads, url },
  } = asset;
  const iconProps = getIconPropsSearchResult({ type: ACTIONS_ICON[resultItem.type], colors: theme.colors });
  const LINK_COPIED = I18n.t('js.learner.libraries.link_copied');
  const COPY_LINK = I18n.t('js.learner.libraries.copy_link');
  const DOWNLOAD_FILE = I18n.t('js.learner.libraries.download_file');
  const DOWNLOADING = I18n.t('js.admin.cms.downloading');
  const [isCopied, setIsCopied] = useState(false);
  const createCmsAssetAuditData = useCreateAssetAuditData();
  const queryClient = useQueryClient();
  const { isDownloading, downloadFile } = useFileDownloader({
    assetId: asset.id,
    name: downloadName,
    onDownloadComplete: () => {
      MixPanel.track(MixpanelEvents.DOWNLOAD_FILE, { location: 'advanced_search' });

      createCmsAssetAuditData.mutate(
        {
          topLevelContentId: asset.topLevelContentId,
          assetId: asset.id,
          assetType: APP_CONSTANTS.CMS_ASSET_TYPES.FILE,
          actionType: CMSAssetAuditAction.DOWNLOAD,
        },
        {
          onSuccess: async () => {
            const keyToInvalidate = [GET_LIBRARY_KEY];
            await queryClient.invalidateQueries(keyToInvalidate);
          },
        },
      );
    },
  });

  const onCopyHandler = () => {
    setIsCopied(true);
    MixPanel.track(MixpanelEvents.COPY_LINK, { location: 'advanced_search' });

    createCmsAssetAuditData.mutate(
      {
        topLevelContentId: asset.topLevelContentId,
        assetId: asset.id,
        assetType: APP_CONSTANTS.CMS_ASSET_TYPES.LINK,
        actionType: CMSAssetAuditAction.DOWNLOAD,
      },
      {
        onSuccess: async () => {
          const keyToInvalidate = [GET_LIBRARY_KEY];
          await queryClient.invalidateQueries(keyToInvalidate);
        },
      },
    );
  };

  const handleDownload = () => {
    if (allowDownloads && !isDownloading) {
      downloadFile();
    }
  };

  if (resultItem.type === CMSTopLevelContentTypes.CMS_FILE) {
    return (
      <>
        <ActionIcon
          iconName={iconProps.name}
          iconColor={iconProps.color}
          label={DOWNLOAD_FILE}
          onPress={handleDownload}
          isDisabled={!allowDownloads || isDownloading}
        />
        <Toast isOpen={isDownloading}>{DOWNLOADING}</Toast>
      </>
    );
  }

  if (resultItem.type === CMSTopLevelContentTypes.CMS_LINK) {
    return (
      <>
        <CopyToClipboard text={url} onCopy={onCopyHandler}>
          <ActionIcon iconName={iconProps.name} iconColor={iconProps.color} label={COPY_LINK} />
        </CopyToClipboard>
        <Toast isOpen={isCopied} onClose={() => setIsCopied(false)}>
          {LINK_COPIED}
        </Toast>
      </>
    );
  }

  return null;
};

export const CMSSearchResult = ({ resultItem, resultTarget = '_self' }: AdvancedSearchResultProps) => {
  const theme = useTheme();
  const { data, isLoading, error } = useGetAssetDetails(TYPE_DICTIONARY[resultItem.type], resultItem.id);
  const dateFormatter = useDateFormatter({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const date = dateFormatter.format(new Date(resultItem.updatedDate));
  const updatedInfo = `Updated ${date} ${resultItem.updatedBy.name}`;
  const downloadsIconName = ACTIONS_ICON[resultItem.type] || 'total_downloads_file';
  const titleText = resultItem.snippetMatch?.title ?? resultItem.name ?? null;
  const descriptionText =
    resultItem.type === CMSTopLevelContentTypes.CMS_LINK
      ? (resultItem.description ?? null)
      : (resultItem.snippetMatch?.subtitle ?? resultItem.description ?? null);

  let totalViews = '0';
  let totalDownloads = '0';

  if (isLoading || error) {
    return null;
  }

  if (data) {
    const { metrics } = data || {};
    if (metrics?.totalViews) {
      totalViews = new Intl.NumberFormat().format(metrics.totalViews);
    }
    if (metrics?.totalDownloads) {
      totalDownloads = new Intl.NumberFormat().format(metrics.totalDownloads);
    }
  }

  return (
    <>
      <Stack size={4}>
        <Stack size={1}>
          <TitleBar>
            <ResultTitle>
              <IconWrapper>
                <ResultIcon type={resultItem.type} theme={theme} />
              </IconWrapper>
              <Button
                variant="text-inline"
                href={getRedirectUrlForSearchResult({ id: resultItem.id, type: resultItem.type })}
                target={resultTarget}
                onPress={() => {
                  MixPanel.track(LearnerSearchMixpanelEvents.LEARNER_ADVANCED_SEARCH_CLICKS_RESULT);
                }}
              >
                <Utility isExpandable level="xl" maxLines={1} as="p">
                  {ReactHtmlParser(titleText)}
                </Utility>
              </Button>
            </ResultTitle>
            <ActionIcons>
              <ActionsBar resultItem={resultItem} asset={data} theme={theme} />
            </ActionIcons>
          </TitleBar>
          <ResultBodyStack size={1}>
            {descriptionText && (
              <Body maxLines={1} level="sm" color={theme.colors.on[900]}>
                {ReactHtmlParser(descriptionText)}
              </Body>
            )}
            <Body level="sm" color={theme.colors.on[600]}>
              <Body level="sm">
                <Row>
                  <Col>{updatedInfo}</Col>
                </Row>
              </Body>
            </Body>
            <FooterWrapper level="sm">
              <Row>
                <InfoWrapper>
                  <ResultIcon type="total_views" theme={theme} />
                  <Body level="sm">{totalViews}</Body>
                </InfoWrapper>
                <InfoWrapper>
                  <ResultIcon type={downloadsIconName} theme={theme} />
                  <Body level="sm">{totalDownloads}</Body>
                </InfoWrapper>
              </Row>
            </FooterWrapper>
          </ResultBodyStack>
        </Stack>
      </Stack>
      <Divider spacing={4} />
    </>
  );
};
